/**
 * ComboboxField — searchable, keyboard-accessible single-select for long option
 * lists (countries, ISO 3166-2 subdivisions). Filters as you type, stores the
 * selected option's `value`, displays its `label`.
 *
 * Hand-rolled (no runtime dependency): the only new behaviour is type-to-filter
 * over a static array plus ARIA combobox wiring, which a library would not
 * meaningfully simplify and would add bundle weight.
 *
 * Integrates with react-hook-form via useController, mirroring the register-based
 * fields in fields.tsx (same name/required contract, same form-input styling,
 * same error/hint markup).
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useController, useFormContext, get as getFromErrors } from 'react-hook-form';
import type { RegisterOptions } from 'react-hook-form';
import { FieldTooltip } from '../FieldTooltip';
import type { FieldTooltipEntry } from '../../lib/fieldTooltips';
import { matchesQuery } from '../../lib/comboboxFilter';

interface Option {
  value: string;
  label: string;
}

interface Props {
  name: string;
  label: string;
  options: Option[];
  hint?: string;
  required?: boolean;
  tooltip?: FieldTooltipEntry;
  placeholder?: string;
  rules?: RegisterOptions;
}

export function ComboboxField({
  name,
  label,
  options,
  hint,
  required,
  tooltip,
  placeholder = 'Select or type to search…',
  rules,
}: Props) {
  const { control, formState } = useFormContext();
  // RC-4: carry a specific "<label> is required" message so the combobox never
  // shows the generic "This field is required." An explicit `rules.required`
  // (buildValidationRules, default-label message matching the submit gate) wins.
  const { field } = useController({
    name,
    control,
    rules: { required: required ? `${label} is required` : false, ...rules },
  });

  const reactId = useId();
  const inputId = `${reactId}-combobox`;
  const listId = `${reactId}-listbox`;
  const hintId = `${reactId}-hint`;
  const errorId = `${reactId}-error`;

  const errObj = getFromErrors(formState.errors, name);
  const errorMessage =
    errObj && typeof errObj.message === 'string' && errObj.message
      ? errObj.message
      : errObj?.type === 'required'
        ? 'This field is required.'
        : errObj
          ? 'Please check this field.'
          : null;

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === field.value)?.label ?? '',
    [options, field.value],
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // When closed, the input shows the selected label; when open, it shows the
  // live query the user is typing.
  const inputValue = open ? query : selectedLabel;

  const filtered = useMemo(
    () => options.filter((o) => matchesQuery(o.label, query)),
    [options, query],
  );

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function commit(opt: Option) {
    field.onChange(opt.value);
    setOpen(false);
    setQuery('');
  }

  function openMenu() {
    if (!open) {
      setOpen(true);
      setQuery('');
      const idx = filtered.findIndex((o) => o.value === field.value);
      setActiveIndex(idx >= 0 ? idx : 0);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) return openMenu();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return openMenu();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[activeIndex]) {
        e.preventDefault();
        commit(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setQuery('');
      }
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="form-label" htmlFor={inputId}>
        {label}
        {required ? (
          <>
            <span aria-hidden="true" className="text-[#FB8E28] ml-0.5">*</span>
            <span className="sr-only"> (required)</span>
          </>
        ) : (
          <span className="form-label-optional">(optional)</span>
        )}
        {tooltip && (
          <FieldTooltip
            description={tooltip.description}
            examples={tooltip.examples}
            ariaLabel={`Help for ${label}`}
          />
        )}
      </label>

      <div className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={open && filtered[activeIndex] ? `${listId}-opt-${activeIndex}` : undefined}
          aria-required={required || undefined}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={[hint ? hintId : null, errorMessage ? errorId : null].filter(Boolean).join(' ') || undefined}
          className="form-input pr-10"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            if (!open) setOpen(true);
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          onFocus={openMenu}
          onClick={openMenu}
          onKeyDown={onKeyDown}
          onBlur={field.onBlur}
        />
        <svg
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path d="M6 8l4 4 4-4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-300 bg-white py-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-slate-400">No matches</li>
          ) : (
            filtered.map((opt, i) => {
              const isActive = i === activeIndex;
              const isSelected = opt.value === field.value;
              return (
                <li
                  key={opt.value}
                  id={`${listId}-opt-${i}`}
                  role="option"
                  aria-selected={isSelected}
                  // onMouseDown (not onClick) so it fires before the input blur closes the list.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commit(opt);
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`cursor-pointer px-3 py-2 text-sm ${
                    isActive ? 'bg-[#EBF7FD] text-[#0B2341]' : 'text-slate-700'
                  } ${isSelected ? 'font-semibold' : ''}`}
                >
                  {opt.label}
                </li>
              );
            })
          )}
        </ul>
      )}

      {hint && <p id={hintId} className="form-hint">{hint}</p>}
      {errorMessage && <p id={errorId} className="form-error" role="alert">{errorMessage}</p>}
    </div>
  );
}
